/**
 * 새 버전 자동 업데이트 — "지금 새로고침해도 사용자 작업이 안 날아가나" 판정 규칙.
 *
 * 왜 있나: 배포 뒤 옛 탭을 새 코드로 갈아끼우는 방법은 새로고침뿐인데, 새로고침은 **브라우저
 * 메모리에만 있고 아직 서버에 저장 안 된 것**을 전부 지운다. 그래서 "안전하면 조용히 자동,
 * 위험하면 물어본다"로 가른다(2026-09-05 결정). 신호를 모으는 건 브라우저 쪽(src/lib/updateSafety.ts)이고,
 * 여기는 모인 신호로 판정만 한다 — 순수 함수라 Node 테스트로 규칙을 고정할 수 있다.
 *
 * 규칙은 **위험 쪽으로 기울여** 둔다. 안전한데 물어보는 건 클릭 한 번이지만, 위험한데 자동으로
 * 넘기면 작업이 날아간다.
 */

/** 새로고침을 막는 이유. 모달에 "왜 자동으로 안 넘겼나"를 사람 말로 보여 주는 데도 쓴다. */
export type UnsafeReason =
  | "customer-page" // 고객이 직접 채우는 공개 화면(신청서·서명·페이백) — 저장 전 입력이 통째로 날아간다
  | "guarded-page" // 화면이 스스로 "떠나면 잃는다"고 선언(beforeunload 로 막는 화면 — 시세표 편집기 등)
  | "modal-open" // 팝업창이 열려 있다 — 대개 입력·선택·확인 중인 자리
  | "editing" // 입력칸에 커서가 있다 — 지금 치는 중일 가능성
  | "request-in-flight" // 저장·업로드 요청이 서버 응답을 기다리는 중
  | "recent-activity"; // 방금 클릭·타이핑했다 — 손이 올라가 있는 동안 화면이 바뀌면 오조작

export interface UpdateSafetySignals {
  /** 현재 주소 경로(pathname). */
  pathname: string;
  /** 화면이 beforeunload 를 막았나(가짜 이벤트를 쏴서 defaultPrevented 를 본 결과). */
  unloadGuarded: boolean;
  /** 열려 있는 모달(팝업) 수 — 새 버전 모달 자신은 세지 않는다. */
  openModals: number;
  /** 입력칸(input·textarea·select·contenteditable)에 포커스가 있나. */
  editing: boolean;
  /** 진행 중인 저장·업로드 요청 수(GET 이 아닌 fetch + react-query mutation). */
  inFlightWrites: number;
  /** 마지막 클릭·타이핑으로부터 지난 시간(ms). 한 번도 없었으면 Infinity. */
  msSinceActivity: number;
}

/** 마지막 조작 뒤 이만큼은 조용해야 "손이 비었다"로 본다. */
export const IDLE_MS = 10 * 1000;

/**
 * 고객이 직접 채우는 공개 화면 — 로그인 없이 링크로 열고, 저장 전까지 입력이 브라우저에만 있다.
 * 여기서는 자동으로 절대 안 넘긴다(포커스·조작 신호만으로는 "폰 키보드를 닫고 잠깐 멈춘" 고객을
 * 못 가른다). 읽기만 하는 공개 화면(시세표 공유 /quote, 앱 안내 /msg-forwarder, 로그인)은 뺀다.
 */
const CUSTOMER_INPUT_PREFIXES = [
  "/form/",
  "/form-simple/",
  "/f/",
  "/apply",
  "/signature-pad/",
  "/crm/signature-pad",
  "/policy-upload/",
  "/payback/",
  "/payback-apply/",
];

export function isCustomerInputPage(pathname: string): boolean {
  return CUSTOMER_INPUT_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

export interface UpdateSafetyVerdict {
  safe: boolean;
  reasons: UnsafeReason[];
}

/** 신호를 보고 "지금 새로고침해도 되나"를 판정한다. 이유는 심각한 순서대로 담긴다. */
export function assessUpdateSafety(s: UpdateSafetySignals): UpdateSafetyVerdict {
  const reasons: UnsafeReason[] = [];
  if (isCustomerInputPage(s.pathname)) reasons.push("customer-page");
  if (s.unloadGuarded) reasons.push("guarded-page");
  if (s.openModals > 0) reasons.push("modal-open");
  if (s.editing) reasons.push("editing");
  if (s.inFlightWrites > 0) reasons.push("request-in-flight");
  if (s.msSinceActivity < IDLE_MS) reasons.push("recent-activity");
  return { safe: reasons.length === 0, reasons };
}

/** 모달에 보여 줄 사람 말. 첫 번째(가장 심각한) 이유만 쓴다. */
export const UNSAFE_REASON_LABEL: Record<UnsafeReason, string> = {
  "customer-page": "입력 중인 신청 내용이 있을 수 있어",
  "guarded-page": "이 화면에 저장하지 않은 작업이 있어",
  "modal-open": "창이 열려 있어",
  editing: "입력 중인 내용이 있어",
  "request-in-flight": "저장·업로드가 진행 중이라",
  "recent-activity": "지금 작업 중인 것 같아",
};
